/**
 * errorReviewService.ts
 * Servico centralizado para gravacao, consulta e atualizacao do Caderno de Erros Inteligente.
 */
import { dbManager } from './db_manager';

export interface ExerciseErrorRecord {
  id: string;
  user_id: string;
  language: string;
  exercise_type: 'quizzes' | 'flashcards' | 'pronunciation' | 'writing' | 'listening';
  source_origin: 'roadmap' | 'direct_url';
  item_id: string;
  item_payload_json: any;
  last_user_input?: string;
  error_count: number;
  consecutive_correct: number;
  status: 'pending' | 'mastered';
  last_failed_at: string;
  mastered_at?: string;
  next_review_date?: string;
}

export const errorReviewService = {
  /**
   * Registra ou atualiza um erro de exercicio (Event-Driven UPSERT)
   */
  async recordError(params: {
    userId: string | undefined;
    language: string;
    exerciseType: 'quizzes' | 'flashcards' | 'pronunciation' | 'writing' | 'listening';
    sourceOrigin?: 'roadmap' | 'direct_url';
    itemId: string;
    itemPayload: any;
    userInput?: string;
  }): Promise<void> {
    const { userId, language, exerciseType, sourceOrigin = 'direct_url', itemId, itemPayload, userInput } = params;
    if (!userId || userId === 'guest' || !language || !exerciseType || !itemId) return;

    try {
      const cleanLang = (language || 'en').toLowerCase().split('-')[0];
      const cleanType = exerciseType.toLowerCase() as any;
      const cleanOrigin = sourceOrigin.toLowerCase();

      await dbManager.execute({
        action: 'record_exercise_error',
        table: 'user_exercise_errors',
        user_id: userId,
        item_id: itemId.toString(),
        language: cleanLang,
        exercise_type: cleanType,
        source_origin: cleanOrigin,
        item_payload_json: typeof itemPayload === 'string' ? itemPayload : JSON.stringify(itemPayload || {}),
        last_user_input: userInput || '',
        data: {
          user_id: userId,
          item_id: itemId.toString(),
          language: cleanLang,
          exercise_type: cleanType,
          source_origin: cleanOrigin,
          item_payload_json: typeof itemPayload === 'string' ? itemPayload : JSON.stringify(itemPayload || {}),
          last_user_input: userInput || ''
        }
      });
    } catch (err) {
      console.warn('[errorReviewService] Error recording exercise error:', err);
    }
  },

  /**
   * Busca os erros pendentes para o usuario agendados para hoje ou dias anteriores (next_review_date <= TODAY)
   */
  async getPendingErrors(
    userId: string | undefined,
    language?: string,
    exerciseType?: string
  ): Promise<ExerciseErrorRecord[]> {
    if (!userId || userId === 'guest') return [];

    try {
      const cleanLang = language ? language.toLowerCase().split('-')[0] : 'all';
      const cleanType = exerciseType ? exerciseType.toLowerCase() : 'all';

      const res = await dbManager.execute({
        action: 'get_pending_exercise_errors',
        table: 'user_exercise_errors',
        user_id: userId,
        language: cleanLang,
        exercise_type: cleanType,
        data: {
          user_id: userId,
          language: cleanLang,
          exercise_type: cleanType
        }
      });

      const rawList = res && res.data ? res.data : (Array.isArray(res) ? res : []);
      return rawList.map((item: any) => {
        let payload = item.item_payload_json;
        if (typeof payload === 'string') {
          try {
            payload = JSON.parse(payload);
          } catch (e) {}
        }
        return {
          ...item,
          item_payload_json: payload
        };
      });
    } catch (err) {
      console.warn('[errorReviewService] Error fetching pending exercise errors:', err);
      return [];
    }
  },

  /**
   * Atualiza a revisao do erro (acerto vs falha) com a regra de repeticao espacada de 3 etapas
   */
  async submitReviewResult(
    errorId: string,
    isCorrect: boolean,
    userId?: string
  ): Promise<void> {
    if (!errorId || !userId || userId === 'guest') return;

    try {
      await dbManager.execute({
        action: 'update_exercise_error_review',
        table: 'user_exercise_errors',
        user_id: userId,
        error_id: errorId,
        is_correct: isCorrect,
        data: {
          user_id: userId,
          error_id: errorId,
          is_correct: isCorrect
        }
      });
    } catch (err) {
      console.warn('[errorReviewService] Error updating exercise error review:', err);
    }
  },

  /**
   * Obtem estatisticas gerais de erros do usuario (pendentes vs dominados)
   */
  async getErrorStats(
    userId: string | undefined,
    language?: string
  ): Promise<{ pendingCount: number; masteredCount: number }> {
    if (!userId || userId === 'guest') return { pendingCount: 0, masteredCount: 0 };

    try {
      const pendingList = await this.getPendingErrors(userId, language);

      const cleanLang = language ? language.toLowerCase().split('-')[0] : 'all';
      let masteredRows: any[] = [];

      if (cleanLang === 'all') {
        masteredRows = await dbManager.from('user_exercise_errors', userId).select({
          user_id: userId,
          status: 'mastered'
        });
      } else {
        masteredRows = await dbManager.from('user_exercise_errors', userId).select({
          user_id: userId,
          language: cleanLang,
          status: 'mastered'
        });
      }

      return {
        pendingCount: pendingList.length,
        masteredCount: Array.isArray(masteredRows) ? masteredRows.length : 0
      };
    } catch (err) {
      console.warn('[errorReviewService] Error fetching error stats:', err);
      return { pendingCount: 0, masteredCount: 0 };
    }
  }
};
